import { Validators } from "@angular/forms";
import { endOfDay, format, parseISO, subDays } from "date-fns";
import { debounceTime, distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs';
import { InvoiceOcr } from "../../invoice-ocr/invoice-ocr.service";
import { ClaimControls, ClaimForm } from "../form.controls";
import { FormFactory } from "../form.factory";
import { Claim } from "../form.resources";
import { claimAmountMaxHufValidator } from "../validators/claim-amount-huf";
import { maxDateValidator } from "../validators/max-date";
import { numberValidator } from "../validators/number";

export function createClaimSaveQueueKey(claim?: Claim): string {
  return claim?.id ?? crypto.randomUUID();
}

export function getClaimSaveQueueKey(form: ClaimForm): string {
  return form.controls.save_queue_key.value;
}

export function getClaimForm(this: FormFactory, claim?: Claim): ClaimForm {
  const isEditable = claim?.is_editable !== false;
  const isDeletable = claim?.is_deletable !== false;

  const maxDueDate = endOfDay(subDays(new Date(), 1));
  const form = this.fb.group<ClaimControls>({
    file: this.fb.control(
      claim?.file,
      isEditable ? [Validators.required] : [],
    ),
    original_amount: this.fb.control(
      claim?.original_amount ?? 0,
      {
        nonNullable: true,
        validators: [
          Validators.required,
          numberValidator,
          Validators.min(1),
          claimAmountMaxHufValidator(this.formService),
        ],
      },
    ),
    currency_iso: this.fb.control(
      claim?.currency_iso || 'HUF',
      {
        nonNullable: true,
        validators: [Validators.required],
      },
    ),
    due_date_at: this.fb.control(
      claim?.due_date_at
        ? (claim.due_date_at instanceof Date
          ? claim.due_date_at
          : parseISO(claim.due_date_at))
        : null,
      [Validators.required, maxDateValidator(maxDueDate)],
    ),

    // invoice data
    issued_at: this.fb.control(claim?.issued_at ?? null),
    invoice_number: this.fb.control(claim?.invoice_number ?? null),
    claim_type_internal_id: this.fb.control(claim?.claim_type_internal_id ?? null),

    // data for convenience
    id: this.fb.control(
      claim?.id ?? (this.formService.isGuest ? new Date().valueOf().toString() : ''),
      { nonNullable: true },
    ),
    save_queue_key: this.fb.control(createClaimSaveQueueKey(claim), { nonNullable: true }),
    is_deletable: this.fb.control(isDeletable, { nonNullable: true }),
    is_editable: this.fb.control(isEditable, { nonNullable: true }),
  });

  if (isEditable === false) {
    Object.values(form.controls).forEach(control => {
      if (control.valid) {
        control.disable({ onlySelf: true });
      }
    });
  }

  const currency_iso = form.controls.currency_iso;
  const original_amount = form.controls.original_amount;
  currency_iso.valueChanges.subscribe(() => {
    original_amount.updateValueAndValidity({ emitEvent: false });
  });
  original_amount.valueChanges
    .pipe(
      startWith(original_amount.value),
    )
    .subscribe({
      next: value => {
        if (typeof value === 'number') {
          if (value === 0 || Number.isNaN(value)) {
            original_amount.setValue('');
          }
          return;
        }
        if (!value) {
          return;
        }

        if (typeof value === 'string') {
          original_amount.setValue(+value.replace(/\D/g, ''));
        } else {
          console.warn('Unexpected type');
          original_amount.setValue('');
        }
      },
    });

  const due_date_at = form.controls.due_date_at;

  this.formService.claimExchangeRates
    .pipe(takeUntil(this.formService.formChange))
    .subscribe(() => form.updateValueAndValidity());

  form.statusChanges
    .pipe(
      debounceTime(1000),
      filter(() => form.valid && !!original_amount?.enabled && due_date_at.enabled),
      map(() => JSON.stringify(form.value)),
      distinctUntilChanged(),
    )
    .subscribe({
      next: () => {
        this.formService.upsertClaim(form);
      }
    });

  return form;
}

export function isClaimMatchingInvoiceOcr(
  claim: Claim,
  invoice: InvoiceOcr,
): boolean {
  return (!claim.currency_iso || claim.currency_iso === invoice.currency_iso)
    && (!claim.original_amount || claim.original_amount === invoice.claim_original_amount)
    && (!claim.due_date_at || (
      claim.due_date_at instanceof Date
        ? format(claim.due_date_at, 'yyyy-MM-dd')
        : claim.due_date_at
    ) === invoice.claim_due_date_at);
}

export function mapInvoiceOcrToClaim(
  invoice: InvoiceOcr,
  file: File,
): Partial<Claim> {
  const claim: Partial<Claim> = {
    file,
  };
  if (invoice.currency_iso) {
    claim.currency_iso = invoice.currency_iso;
  }
  if (invoice.claim_original_amount) {
    claim.original_amount = invoice.claim_original_amount;
  }
  if (invoice.claim_due_date_at) {
    claim.due_date_at = parseISO(invoice.claim_due_date_at);
  }
  if (invoice.invoice_number) {
    claim.invoice_number = invoice.invoice_number;
  }
  if (invoice.invoice_issued_at) {
    claim.issued_at = invoice.invoice_issued_at;
  }
  if (invoice.claim_type_internal_id) {
    claim.claim_type_internal_id = invoice.claim_type_internal_id;
  }
  return claim;
}

const OCR_CLAIM_FIELD_NAMES = [
  'file',
  'original_amount',
  'currency_iso',
  'due_date_at',
  'invoice_number',
  'issued_at',
  'claim_type_internal_id',
] as const satisfies readonly (keyof ClaimControls)[];

export function patchClaimByInvoiceOcr(
  claimForm: ClaimForm,
  invoice: InvoiceOcr,
  file: File,
): void {
  claimForm.patchValue(mapInvoiceOcrToClaim(invoice, file));

  for (const fieldName of OCR_CLAIM_FIELD_NAMES) {
    const control = claimForm.controls[fieldName];
    if (control.disabled) {
      continue;
    }

    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
  }

  claimForm.markAsDirty();
  claimForm.updateValueAndValidity({ emitEvent: false });
}
